import React from 'react';
import { useRecordContext } from 'react-admin';
import { useWatch } from 'react-hook-form';
import Card from '@mui/material/Card';

import PermissionCategoryAccordion from './PermissionCategoryAccordion';
import PermissionSubCategoryAccordion from './PermissionSubCategoryAccordion';
import PermissionRow from './PermissionRow';
import { ReformedPermissionRole, ReformedPermissions } from '../type';
import permissionCategories, { Permission, PermissionCategory } from './permissionCategories';

const getCountPermissionsSubCategory = (
  permissions: Permission[],
  recordPermissions?: ReformedPermissions,
) => permissions.filter((permission) => (recordPermissions
  ? recordPermissions[permission.action]?.enabled : false)).length;

const getTotalPermissionCategory = (
  category: PermissionCategory,
) => category.subCategories.reduce((total, subCategory) => (
  total + subCategory.permissions.length
), 0);

const getCountPermissionCategory = (
  category: PermissionCategory,
  recordPermissions: ReformedPermissions,
) => category.subCategories.reduce((total, subCategory) => (
  total + getCountPermissionsSubCategory(subCategory.permissions, recordPermissions)
), 0);

const PermissionAccordion = ({
  mode,
  selectedPermissions,
}: {
  mode: 'show' | 'creation';
  selectedPermissions: ReformedPermissions;
}) => (
  <Card variant="outlined">
    {permissionCategories.map((category) => (
      <PermissionCategoryAccordion
        title={category.name}
        key={category.name}
        count={getCountPermissionCategory(category, selectedPermissions)}
        total={getTotalPermissionCategory(category)}
      >
        {category.subCategories.map(({
          name: subCategoryName,
          permissions,
        }) => (
          <PermissionSubCategoryAccordion
            key={subCategoryName}
            title={subCategoryName}
            count={getCountPermissionsSubCategory(permissions, selectedPermissions)}
            total={permissions.length}
          >
            {permissions.map((permission) => (
              <PermissionRow
                key={permission.action}
                action={permission.action}
                label={permission.description}
                extraInformation={permission.extraInformation}
                mode={mode}
              />
            ))}
          </PermissionSubCategoryAccordion>
        ))}
      </PermissionCategoryAccordion>
    ))}
  </Card>
);

export const PermissionAccordionShow = () => {
  const record = useRecordContext<ReformedPermissionRole>();
  const selectedPermissions = record?.permissions;
  if (!selectedPermissions) return null;

  return (
    <PermissionAccordion mode="show" selectedPermissions={selectedPermissions} />
  );
};

export const PermissionAccordionCreate = () => {
  const selectedPermissions = useWatch({ name: 'permissions' }) as ReformedPermissions | undefined;

  return (
    <PermissionAccordion mode="creation" selectedPermissions={selectedPermissions ?? {}} />
  );
};

export default PermissionAccordion;
